/* Kenra Language / Region Modal
 * Custom element wrapping a <dialog> that lets the user pick their language and
 * country. Each option submits the matching {% form 'localization' %}, which
 * reloads the page in that locale / country (Shopify's native localization).
 *
 * Open it from any element carrying [data-language-region-trigger] (the
 * "Language / Region" nav + drawer items). The handler is delegated and
 * registered once inside the customElements.get() guard so it stays single even
 * if the snippet is rendered more than once.
 */

if (!customElements.get('kenra-language-region-modal')) {
  class KenraLanguageRegionModal extends HTMLElement {
    constructor() {
      super();
      this.dialog = this.querySelector('dialog');
      this.closeBtn = this.querySelector('.kenra-lang-region__close');

      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }

      if (this.dialog) {
        // Click on the backdrop (the dialog element itself, not the card) closes.
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
        // Esc fires "cancel" before "close" — intercept so we clean up the lock.
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
      }

      // Option clicks → set the form's hidden input + submit (page reloads).
      this.querySelectorAll('[data-locale-value]').forEach((btn) => {
        btn.addEventListener('click', () =>
          this.submitLocalization(btn, 'locale_code', btn.dataset.localeValue)
        );
      });
      this.querySelectorAll('[data-country-value]').forEach((btn) => {
        btn.addEventListener('click', () =>
          this.submitLocalization(btn, 'country_code', btn.dataset.countryValue)
        );
      });
    }

    submitLocalization(btn, inputName, value) {
      const form = btn.closest('form');
      if (!form) return;
      const input = form.querySelector(`input[name="${inputName}"]`);
      if (input) input.value = value;
      form.submit();
    }

    open(returnFocusEl) {
      if (!this.dialog || this.dialog.open) return;
      // Element to focus on close. <dialog> restores focus to whatever was
      // focused at open() time, but when opened from the mobile drawer that
      // trigger gets hidden (drawer closes), so focus would fall to <body>.
      // Capture a stable, still-visible element instead.
      this._returnFocusEl = returnFocusEl || null;
      this.dialog.showModal();
      document.documentElement.classList.add('kenra-lang-region-open');
      // Move initial focus to the (non-interactive) card so no control shows a
      // focus ring on open — matches the live modal. Keyboard users still get
      // rings once they Tab to the close button / options.
      const card = this.querySelector('.kenra-lang-region__card');
      if (card) card.focus();
    }

    close() {
      if (this.dialog && this.dialog.open) this.dialog.close();
      document.documentElement.classList.remove('kenra-lang-region-open');
      // Restore focus to the captured element if it's still visible (the native
      // dialog return target may have been hidden with the drawer).
      const el = this._returnFocusEl;
      this._returnFocusEl = null;
      if (el && el.isConnected && el.offsetParent !== null) el.focus();
    }
  }

  customElements.define('kenra-language-region-modal', KenraLanguageRegionModal);

  // Delegated trigger handler — opens the modal and closes the mobile drawer
  // first so the modal isn't rendered behind it.
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-language-region-trigger]');
    if (!trigger) return;

    const modal = document.getElementById('kenra-language-region-modal');
    if (!modal || typeof modal.open !== 'function') return;

    event.preventDefault();

    // If opened from the mobile drawer, close it first and return focus to the
    // hamburger summary (which stays visible) rather than the trigger that the
    // drawer just hid.
    let returnFocusEl = trigger;
    const drawer = document.getElementById('Details-menu-drawer-container');
    if (drawer && drawer.open) {
      drawer.open = false;
      document.documentElement.classList.remove('kenra-drawer-open');
      returnFocusEl = drawer.querySelector('summary') || trigger;
    }

    modal.open(returnFocusEl);
  });
}
